import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks, Task } from '../../crm-tasks/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import GenericListWidget from './GenericListWidget';
import { getDateRangeFromTimeRange } from '../contexts/DashboardFiltersContext';
import type { TimeRange } from './TimeRangeSelector';
import type { DashboardScope } from '../contexts/DashboardFiltersContext';

export interface HighPriorityTasksProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  timeRange?: TimeRange;
  scope?: DashboardScope;
}

const HighPriorityTasks: React.FC<HighPriorityTasksProps> = ({
  limit = 5,
  embedded,
  settings,
  timeRange = 'month',
  scope = 'myWork',
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { user } = useAuth();
  const { tasks, isLoading } = useTasks();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { startDate, endDate } = React.useMemo(() => getDateRangeFromTimeRange(timeRange), [timeRange]);

  const items = React.useMemo(() => {
    const arr = Array.isArray(tasks) ? tasks : [];
    return arr
      .filter((t: Task) => {
        if (scope === 'myWork' && user?.id != null && t.taskAssignedTo !== user.id) return false;
        const taskDate = t.taskDate || t.taskCreatedAt;
        if (taskDate && timeRange !== 'all') {
          const d = new Date(taskDate);
          if (d < startDate || d > endDate) return false;
        }
        const statusDescr = t.taskStatusDescr?.ts_descr?.toLowerCase() || '';
        const isPending = statusDescr.includes('not started') || statusDescr.includes('in progress');
        const isHighPriority = (t.taskPriority ?? 0) >= 3;
        return isPending && isHighPriority;
      })
      .sort((a: Task, b: Task) => {
        const pa = a.taskPriority ?? 0;
        const pb = b.taskPriority ?? 0;
        if (pb !== pa) return pb - pa;
        const da = new Date(a.taskDate || a.taskCreatedAt || 0).getTime();
        const db = new Date(b.taskDate || b.taskCreatedAt || 0).getTime();
        return da - db;
      })
      .slice(0, effectiveLimit);
  }, [tasks, effectiveLimit, scope, user?.id, timeRange, startDate, endDate]);

  return (
    <GenericListWidget<Task>
      title="High Priority Tasks"
      viewAllPath="/crm_tasks"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No high priority tasks"
      getItemKey={(t) => t.taskId}
      embedded={embedded}
      renderItem={(task) => {
        const isOverdue =
          task.taskDate &&
          new Date(task.taskDate) < new Date() &&
          (task.taskStatusDescr?.ts_descr?.toLowerCase() || '').indexOf('completed') === -1;
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/crm_tasks/${task.taskId}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/crm_tasks/${task.taskId}`)}
            className={`flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${isOverdue ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
          >
            <div className="flex-1 min-w-0">
              <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
                {task.taskDescription || task.taskSubject || 'Untitled Task'}
              </p>
              <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
                {task.taskDate
                  ? new Date(task.taskDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : ''}
                {task.client?.clientName && ` · ${task.client.clientName}`}
              </p>
            </div>
          </div>
        );
      }}
    />
  );
};

export default HighPriorityTasks;
